import React, { useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import { useCollection } from "../../hooks/useCollection";
import { useFirestore } from "../../hooks/useFirestore";
import { useAuthContext } from "../../hooks/useAuthContext";
import { database, timestamp } from "../../firebase/config";
import "../gestioneDocumenti/GestioneDocumenti.css";

const selectModalStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 10000 }),
};

const parseNumberIT = (value) => {
  if (value == null || value === "") return NaN;
  if (typeof value === "number") return Number.isFinite(value) ? value : NaN;
  const raw = String(value).trim();
  if (!raw) return NaN;
  let normalized = raw.replace(/\s/g, "");
  if (normalized.includes(",")) {
    normalized = normalized.replace(/\./g, "").replace(",", ".");
  }
  const n = Number(normalized);
  return Number.isFinite(n) ? n : NaN;
};

const parseImporto = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return NaN;

  let normalized = raw.replace(/\s/g, "");
  if (normalized.includes(",")) {
    normalized = normalized.replace(/\./g, "").replace(",", ".");
  }

  const n = Number(normalized);
  return Number.isFinite(n) ? n : NaN;
};

const formatCurrency = (value) => {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("it-IT", { style: "currency", currency: "EUR" });
};

const toJsDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === "function") {
    try {
      return value.toDate();
    } catch (_) {
      return null;
    }
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const getMonthKey = (date) => {
  const d = toJsDate(date);
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
};

export default function WorkOrders({ prj }) {
  const { user } = useAuthContext();
  const { addDocument, deleteDocument, updateDocument } =
    useFirestore("ricavi");
  const { documents: consuntivi } = useCollection(
    "consuntivi",
    null,
    null,
    null,
    false,
  );

  const projectId = (prj?.id ?? "").toString();
  const projectCodice = (prj?.codice ?? "").toString().trim();
  const projectNome =
    (prj?.nome ?? "").toString().trim() ||
    (prj?.dettagli ?? "").toString().trim() ||
    "Senza nome";

  const query = useMemo(() => {
    if (projectCodice) return ["settore", "==", projectCodice];
    if (projectId) return ["projectId", "==", projectId];
    return null;
  }, [projectCodice, projectId]);

  const collectionName = projectCodice || projectId ? "ricavi" : null;
  const { documents, error } = useCollection(collectionName, query, null);

  const sortedDocuments = useMemo(() => {
    const rows = Array.isArray(documents) ? documents : [];
    return rows
      .slice()
      .sort(
        (a, b) =>
          (toJsDate(b?.createdAt)?.getTime() || 0) -
          (toJsDate(a?.createdAt)?.getTime() || 0),
      );
  }, [documents]);

  const [searchTerm, setSearchTerm] = useState("");
  const [filterMeseCompetenza, setFilterMeseCompetenza] = useState("");
  const [onlyNoInvoice, setOnlyNoInvoice] = useState(false);
  const [showFattureTable, setShowFattureTable] = useState(true);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [isSavingAdd, setIsSavingAdd] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [isSavingImport, setIsSavingImport] = useState(false);
  const [deletingId, setDeletingId] = useState("");

  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [bulkNrFattura, setBulkNrFattura] = useState("");
  const [bulkError, setBulkError] = useState("");
  const [isAssigningInvoice, setIsAssigningInvoice] = useState(false);
  const selectAllRef = useRef(null);

  const [formError, setFormError] = useState("");
  const [editError, setEditError] = useState("");
  const [importError, setImportError] = useState("");
  const [editingRowId, setEditingRowId] = useState("");
  const [consuntiviMonthFilter, setConsuntiviMonthFilter] = useState(
    getMonthKey(new Date()),
  );
  const [selectedConsuntivoId, setSelectedConsuntivoId] = useState("");
  const [importRows, setImportRows] = useState([]);
  const [importIsLoading, setImportIsLoading] = useState(false);
  const [importMeseCompetenza, setImportMeseCompetenza] = useState("");

  const [formState, setFormState] = useState({
    cliente: "",
    nrFattura: "",
    numero_documento: "",
    luogoIntervento: "",
    meseCompetenza: "",
    descrizione: "",
    unitaMisura: "",
    importo: "",
  });

  const [editState, setEditState] = useState({
    cliente: "",
    nrFattura: "",
    numero_documento: "",
    luogoIntervento: "",
    meseCompetenza: "",
    descrizione: "",
    unitaMisura: "",
    importo: "",
  });

  useEffect(() => {
    if (!showAddModal && !showEditModal && !showImportModal) return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [showAddModal, showEditModal, showImportModal]);

  useEffect(() => {
    if (!showAddModal && !showEditModal && !showImportModal) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        if (showImportModal) {
          setShowImportModal(false);
          setImportError("");
          setSelectedConsuntivoId("");
          setImportRows([]);
          setIsSavingImport(false);
          setImportIsLoading(false);
        } else {
          setShowAddModal(false);
          setShowEditModal(false);
          setFormError("");
          setEditError("");
        }
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showAddModal, showEditModal, showImportModal]);

  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const rows = Array.isArray(sortedDocuments) ? sortedDocuments : [];
    const filteredByMonth = rows.filter((r) => {
      if (!filterMeseCompetenza) return true;
      const month =
        (r?.meseCompetenza ?? "").toString().trim() ||
        getMonthKey(r?.createdAt);
      return month === filterMeseCompetenza;
    });

    const filteredByInvoice = filteredByMonth.filter((r) => {
      if (!onlyNoInvoice) return true;
      const nr = (r?.nrFattura ?? "").toString().trim();
      return !nr;
    });

    if (!term) return filteredByInvoice;

    return filteredByInvoice.filter((r) => {
      const cliente = (r?.cliente ?? "").toString().toLowerCase();
      const nrFattura = (r?.nrFattura ?? "").toString().toLowerCase();
      const rif = (r?.numero_documento ?? "").toString().toLowerCase();
      const luogo = (r?.luogoIntervento ?? "").toString().toLowerCase();
      const descrizione = (r?.descrizione ?? "").toString().toLowerCase();
      const um = (r?.unitaMisura ?? "").toString().toLowerCase();
      return (
        cliente.includes(term) ||
        nrFattura.includes(term) ||
        rif.includes(term) ||
        luogo.includes(term) ||
        descrizione.includes(term) ||
        um.includes(term)
      );
    });
  }, [filterMeseCompetenza, onlyNoInvoice, searchTerm, sortedDocuments]);

  const filteredTotalImporto = useMemo(() => {
    return (Array.isArray(filteredRows) ? filteredRows : []).reduce(
      (acc, r) => {
        const n =
          typeof r?.importo === "number"
            ? r.importo
            : parseNumberIT(r?.importo);
        return acc + (Number.isFinite(n) ? n : 0);
      },
      0,
    );
  }, [filteredRows]);

  const filteredRowIdSet = useMemo(
    () => new Set((filteredRows || []).map((r) => r.id)),
    [filteredRows],
  );

  useEffect(() => {
    setSelectedRowIds((prev) => prev.filter((id) => filteredRowIdSet.has(id)));
  }, [filteredRowIdSet]);

  const allVisibleSelected = useMemo(() => {
    if (!filteredRows || filteredRows.length === 0) return false;
    return filteredRows.every((r) => selectedRowIds.includes(r.id));
  }, [filteredRows, selectedRowIds]);

  const someVisibleSelected = useMemo(() => {
    if (!filteredRows || filteredRows.length === 0) return false;
    return filteredRows.some((r) => selectedRowIds.includes(r.id));
  }, [filteredRows, selectedRowIds]);

  useEffect(() => {
    if (!selectAllRef.current) return;
    selectAllRef.current.indeterminate =
      someVisibleSelected && !allVisibleSelected;
  }, [allVisibleSelected, someVisibleSelected]);

  const fattureRaggruppate = useMemo(() => {
    const groups = new Map();
    (Array.isArray(filteredRows) ? filteredRows : []).forEach((r) => {
      const key = (r?.nrFattura ?? "").toString().trim();
      if (!key) return;
      const prev = groups.get(key) || {
        nrFattura: key,
        cliente: "",
        totale: 0,
        count: 0,
      };
      const cliente = (r?.cliente ?? "").toString().trim();
      if (!prev.cliente && cliente) prev.cliente = cliente;
      const imp =
        typeof r?.importo === "number" ? r.importo : Number(r?.importo);
      prev.totale += Number.isFinite(imp) ? imp : 0;
      prev.count += 1;
      groups.set(key, prev);
    });
    return Array.from(groups.values()).sort((a, b) =>
      a.nrFattura.localeCompare(b.nrFattura, "it"),
    );
  }, [filteredRows]);

  const openNewModal = () => {
    setFormError("");
    setIsSavingAdd(false);
    setFormState({
      cliente: (prj?.cliente ?? "").toString(),
      nrFattura: "",
      numero_documento: "",
      luogoIntervento: (prj?.localizzazione ?? "").toString(),
      meseCompetenza: getMonthKey(new Date()),
      descrizione: "Lavori eseguiti come da ordine ",
      unitaMisura: "nr",
      importo: "",
    });
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setFormError("");
    setIsSavingAdd(false);
  };

  const openImportModal = () => {
    setImportError("");
    setIsSavingImport(false);
    setConsuntiviMonthFilter(getMonthKey(new Date()));
    setSelectedConsuntivoId("");
    setImportRows([]);
    setImportIsLoading(false);
    setImportMeseCompetenza(getMonthKey(new Date()));
    setShowImportModal(true);
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    setImportError("");
    setIsSavingImport(false);
    setSelectedConsuntivoId("");
    setImportRows([]);
    setImportIsLoading(false);
  };

  const openEditModal = (row) => {
    if (!row?.id) return;
    setEditError("");
    setIsSavingEdit(false);
    setEditingRowId(row.id);
    setEditState({
      cliente: (row?.cliente ?? "").toString(),
      nrFattura: (row?.nrFattura ?? "").toString(),
      numero_documento: (row?.numero_documento ?? "").toString(),
      luogoIntervento: (row?.luogoIntervento ?? "").toString(),
      meseCompetenza:
        (row?.meseCompetenza ?? "").toString().trim() ||
        getMonthKey(row?.createdAt) ||
        getMonthKey(new Date()),
      descrizione: (row?.descrizione ?? "").toString(),
      unitaMisura: (row?.unitaMisura ?? "").toString(),
      importo:
        row?.importo == null
          ? ""
          : typeof row.importo === "number"
            ? String(row.importo)
            : String(row.importo),
    });
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditError("");
    setEditingRowId("");
    setIsSavingEdit(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    setIsSavingAdd(true);

    const cliente = (prj?.cliente ?? "").toString().trim();
    const nrFattura = "";
    const numeroDocumento = String(formState.numero_documento || "").trim();
    const luogoIntervento = String(formState.luogoIntervento || "").trim();
    const meseCompetenza = String(formState.meseCompetenza || "").trim();
    const descrizione = formState.descrizione.trim();
    const unitaMisura = "nr";
    const importoNumber = parseImporto(formState.importo);

    if (!cliente) {
      setFormError("Cliente non impostato nel progetto.");
      setIsSavingAdd(false);
      return;
    }

    if (!meseCompetenza || !descrizione) {
      setFormError("Compila tutti i campi.");
      setIsSavingAdd(false);
      return;
    }

    if (!Number.isFinite(importoNumber)) {
      setFormError("Importo non valido.");
      setIsSavingAdd(false);
      return;
    }

    const settore = projectCodice || projectId;
    if (!settore) {
      setFormError(
        "Impossibile determinare il progetto da associare al ricavo.",
      );
      setIsSavingAdd(false);
      return;
    }

    try {
      const created = await addDocument({
        cliente,
        nrFattura: nrFattura || "",
        numero_documento: numeroDocumento || "",
        luogoIntervento: luogoIntervento || "",
        meseCompetenza,
        settore,
        descrizione,
        unitaMisura,
        importo: importoNumber,
        projectId: projectId || "",
        projectCodice: projectCodice || "",
        projectNome,
        createdBy: user?.uid || "",
        createdByName: user?.displayName || user?.email || "",
      });

      if (created === null) {
        setFormError("Impossibile salvare il ricavo.");
        return;
      }

      setShowAddModal(false);
    } finally {
      setIsSavingAdd(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditError("");
    setIsSavingEdit(true);

    const id = editingRowId;
    if (!id) {
      setIsSavingEdit(false);
      return;
    }

    const cliente = editState.cliente.trim();
    const nrFattura = String(editState.nrFattura || "").trim();
    const numeroDocumento = String(editState.numero_documento || "").trim();
    const luogoIntervento = String(editState.luogoIntervento || "").trim();
    const meseCompetenza = String(editState.meseCompetenza || "").trim();
    const descrizione = editState.descrizione.trim();
    const unitaMisura = editState.unitaMisura.trim();
    const importoNumber = parseImporto(editState.importo);

    if (!cliente || !meseCompetenza || !descrizione || !unitaMisura) {
      setEditError("Compila tutti i campi.");
      setIsSavingEdit(false);
      return;
    }
    if (!Number.isFinite(importoNumber)) {
      setEditError("Importo non valido.");
      setIsSavingEdit(false);
      return;
    }

    try {
      const updated = await updateDocument(id, {
        cliente,
        nrFattura: nrFattura || "",
        numero_documento: numeroDocumento || "",
        luogoIntervento: luogoIntervento || "",
        meseCompetenza,
        descrizione,
        unitaMisura,
        importo: importoNumber,
        updatedAt: timestamp.fromDate(new Date()),
        updatedBy: user?.uid || "",
        updatedByName: user?.displayName || user?.email || "",
      });

      if (updated === null) {
        setEditError("Impossibile salvare le modifiche.");
        return;
      }

      closeEditModal();
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleDelete = async (row) => {
    if (!row?.id) return;
    if (deletingId) return;
    const label = `${row?.cliente || ""} - ${row?.descrizione || ""}`.trim();
    const ok = window.confirm(
      `Eliminare questo ricavo?${label ? `\n\n${label}` : ""}`,
    );
    if (!ok) return;
    setDeletingId(row.id);
    try {
      await deleteDocument(row.id);
    } finally {
      setDeletingId("");
    }
  };

  const consuntivoOptions = useMemo(() => {
    const rows = Array.isArray(consuntivi) ? consuntivi : [];
    const filtered = rows.filter((c) => {
      const settoreId = (c?.settore?.id ?? c?.settore ?? "").toString().trim();
      const settoreCodice = (c?.settore?.codice ?? "").toString().trim();
      const matchProject =
        (projectId && settoreId && settoreId === projectId) ||
        (projectCodice && settoreCodice && settoreCodice === projectCodice);
      if (!matchProject) return false;
      if (!consuntiviMonthFilter) return true;
      const d = toJsDate(c?.data_creazione);
      const key = getMonthKey(d);
      return key === consuntiviMonthFilter;
    });

    return filtered
      .slice()
      .sort((a, b) => {
        const da = toJsDate(a?.data_creazione);
        const db = toJsDate(b?.data_creazione);
        const ta = da ? da.getTime() : 0;
        const tb = db ? db.getTime() : 0;
        return tb - ta;
      })
      .map((c) => {
        const d = toJsDate(c?.data_creazione);
        const dateLabel = d ? d.toLocaleString("it-IT") : "Senza data";
        const cliente = (c?.cliente ?? "Sconosciuto").toString();
        const luogo = (c?.luogo_lavoro ?? "").toString();
        const tot = c?.totale_complessivo ?? "";
        const label = `${dateLabel} — ${cliente}${luogo ? ` — ${luogo}` : ""}${
          tot ? ` — € ${tot}` : ""
        }`;
        return { value: c.id, label };
      });
  }, [consuntivi, consuntiviMonthFilter, projectCodice, projectId]);

  const selectedConsuntivo = useMemo(() => {
    if (!selectedConsuntivoId) return null;
    return (Array.isArray(consuntivi) ? consuntivi : []).find(
      (c) => c.id === selectedConsuntivoId,
    );
  }, [consuntivi, selectedConsuntivoId]);

  useEffect(() => {
    const load = async () => {
      if (!showImportModal) return;
      if (!selectedConsuntivo) {
        setImportRows([]);
        return;
      }

      setImportIsLoading(true);
      setImportError("");
      try {
        const cliente = (prj?.cliente ?? selectedConsuntivo?.cliente ?? "")
          .toString()
          .trim();
        const docIds = Array.isArray(selectedConsuntivo?.documenti_inclusi)
          ? selectedConsuntivo.documenti_inclusi
          : [];

        const docs = await Promise.all(
          docIds.map(async (id) => {
            try {
              const snap = await database
                .collection("bolle_vendita")
                .doc(id)
                .get();
              if (!snap.exists) return { id, missing: true };
              return { id: snap.id, ...snap.data() };
            } catch (_) {
              return { id, missing: true };
            }
          }),
        );

        const competenzaFromConsuntivo = getMonthKey(
          selectedConsuntivo?.data_creazione,
        );
        const docDates = docs
          .map((d) => toJsDate(d?.data_documento))
          .filter(Boolean)
          .sort((a, b) => a.getTime() - b.getTime());
        const competenzaFromDocs =
          docDates.length > 0 ? getMonthKey(docDates[0]) : "";
        const defaultCompetenza =
          competenzaFromDocs ||
          competenzaFromConsuntivo ||
          getMonthKey(new Date());
        setImportMeseCompetenza(defaultCompetenza);

        const materiali = Array.isArray(selectedConsuntivo?.materiali)
          ? selectedConsuntivo.materiali
          : [];
        const manodopera = Array.isArray(selectedConsuntivo?.manodopera)
          ? selectedConsuntivo.manodopera
          : [];
        const diritti = Array.isArray(selectedConsuntivo?.dirittiChiamata)
          ? selectedConsuntivo.dirittiChiamata
          : [];

        if (docs.length === 0) {
          const total = parseNumberIT(selectedConsuntivo?.totale_complessivo);
          const importo = Number.isFinite(total) ? total : 0;
          setImportRows([
            {
              key: selectedConsuntivo.id,
              docId: "",
              rif: "",
              numeroDocumento: "",
              cliente,
              descrizione: "Consuntivo",
              unitaMisura: "nr",
              importo,
              luogoIntervento: (selectedConsuntivo?.luogo_lavoro ?? "")
                .toString()
                .trim(),
            },
          ]);
          return;
        }

        const rows = docs.map((doc) => {
          const docMateriali = materiali.filter((m) => m?.docId === doc.id);
          const docManodopera = manodopera.filter((m) => m?.docId === doc.id);
          const docDiritti = diritti.filter(
            (d) => d?.docId === doc.id && d?.applicato,
          );

          const totMat = docMateriali.reduce((acc, m) => {
            const q = parseNumberIT(m?.quantita);
            const pu = parseNumberIT(m?.prezzo_unitario);
            if (!Number.isFinite(q) || !Number.isFinite(pu)) return acc;
            return acc + q * pu;
          }, 0);

          const totMan = docManodopera.reduce((acc, m) => {
            const ore = parseNumberIT(m?.totale_ore);
            const tariffa = parseNumberIT(m?.tariffa_oraria);
            if (!Number.isFinite(ore) || !Number.isFinite(tariffa)) return acc;
            return acc + ore * tariffa;
          }, 0);

          const totDir = docDiritti.reduce((acc, d) => {
            const v = parseNumberIT(d?.valore);
            if (!Number.isFinite(v)) return acc;
            return acc + v;
          }, 0);

          const importo = totMat + totMan + totDir;
          const rif = (doc?.id ?? "").toString();
          const numeroDocumento = (doc?.numero_documento ?? "").toString();
          const descrizione = (doc?.oggetto_intervento ?? "").toString().trim();
          const luogoIntervento =
            (doc?.luogo_intervento ?? "").toString().trim() ||
            (selectedConsuntivo?.luogo_lavoro ?? "").toString().trim() ||
            (prj?.localizzazione ?? "").toString().trim();

          return {
            key: doc.id,
            docId: doc.id,
            rif,
            numeroDocumento,
            cliente,
            descrizione: descrizione || "Intervento",
            unitaMisura: "nr",
            importo,
            luogoIntervento,
          };
        });

        setImportRows(rows);
      } catch (e) {
        setImportError("Impossibile caricare i dati del consuntivo.");
        setImportRows([]);
      } finally {
        setImportIsLoading(false);
      }
    };

    load();
  }, [prj, selectedConsuntivo, showImportModal]);

  const handleImport = async () => {
    setImportError("");
    const rows = Array.isArray(importRows) ? importRows : [];
    if (!selectedConsuntivoId) {
      setImportError("Seleziona un consuntivo.");
      return;
    }
    if (rows.length === 0) {
      setImportError("Nessuna riga da importare.");
      return;
    }
    const meseCompetenza = String(importMeseCompetenza || "").trim();
    if (!meseCompetenza) {
      setImportError("Seleziona il mese di competenza.");
      return;
    }
    const invalidImporto = rows.some((r) => !Number.isFinite(r?.importo));
    if (invalidImporto) {
      setImportError("Importo non valido in una o più righe.");
      return;
    }

    const settore = projectCodice || projectId;
    if (!settore) {
      setImportError(
        "Impossibile determinare il progetto da associare al ricavo.",
      );
      return;
    }

    const cliente = (prj?.cliente ?? "").toString().trim();
    if (!cliente) {
      setImportError("Cliente non impostato nel progetto.");
      return;
    }

    try {
      setIsSavingImport(true);
      const batch = database.batch();
      const createdAt = timestamp.fromDate(new Date());
      rows.forEach((r) => {
        const ref = database.collection("ricavi").doc();
        batch.set(ref, {
          cliente,
          nrFattura: "",
          meseCompetenza,
          settore,
          descrizione: r.descrizione,
          unitaMisura: "nr",
          importo: r.importo,
          numero_documento: (r?.numeroDocumento ?? "").toString(),
          luogoIntervento: (r?.luogoIntervento ?? "").toString(),
          projectId: projectId || "",
          projectCodice: projectCodice || "",
          projectNome,
          createdAt,
          createdBy: user?.uid || "",
          createdByName: user?.displayName || user?.email || "",
        });
      });
      await batch.commit();
      closeImportModal();
      alert(`Import completato: ${rows.length} righe create.`);
    } catch (e) {
      setImportError("Errore durante l'import.");
    } finally {
      setIsSavingImport(false);
    }
  };

  const toggleRowSelected = (id) => {
    setBulkError("");
    setSelectedRowIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const toggleSelectAllVisible = () => {
    setBulkError("");
    if (!filteredRows || filteredRows.length === 0) return;
    if (allVisibleSelected) {
      setSelectedRowIds((prev) =>
        prev.filter((id) => !filteredRowIdSet.has(id)),
      );
    } else {
      setSelectedRowIds((prev) => {
        const out = new Set(prev);
        filteredRows.forEach((r) => out.add(r.id));
        return Array.from(out);
      });
    }
  };

  const handleBulkAssignInvoice = async () => {
    setBulkError("");
    const nr = bulkNrFattura.trim();
    if (!nr) {
      setBulkError("Inserisci il numero fattura.");
      return;
    }
    if (!selectedRowIds || selectedRowIds.length === 0) return;

    try {
      setIsAssigningInvoice(true);
      const batch = database.batch();
      const updatedAt = timestamp.fromDate(new Date());
      selectedRowIds.forEach((id) => {
        batch.update(database.collection("ricavi").doc(id), {
          nrFattura: nr,
          updatedAt,
          updatedBy: user?.uid || "",
          updatedByName: user?.displayName || user?.email || "",
        });
      });
      await batch.commit();
      setSelectedRowIds([]);
      setBulkNrFattura("");
    } catch (e) {
      setBulkError("Impossibile assegnare il numero fattura.");
    } finally {
      setIsAssigningInvoice(false);
    }
  };

  return (
    <div className="gestione-documenti">
      <div className="gestione-header">
        <div>
          <h1>
            Ricavi — {projectCodice ? `${projectCodice} ` : ""}
            {projectNome}
          </h1>
          <p>{filteredRows.length} righe</p>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            type="button"
            className="gd-tab active"
            onClick={openNewModal}
          >
            Nuovo ricavo
          </button>
          <button type="button" className="gd-tab" onClick={openImportModal}>
            Import consuntivi
          </button>
        </div>
      </div>

      <div className="gd-filters">
        <div className="gd-filter-row">
          <div className="gd-filter-field gd-search">
            <label>Cerca</label>
            <input
              type="text"
              className="search-input"
              placeholder="Cliente, nr fattura, riferimento, descrizione, UM..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="gd-filter-field">
            <label>Mese competenza</label>
            <div className="gd-month">
              <input
                type="month"
                value={filterMeseCompetenza}
                onChange={(e) => setFilterMeseCompetenza(e.target.value)}
              />
              <button
                type="button"
                className="gd-clear-btn"
                onClick={() => setFilterMeseCompetenza("")}
                disabled={!filterMeseCompetenza}
                aria-label="Rimuovi filtro mese"
                title="Rimuovi filtro mese"
              >
                ×
              </button>
            </div>
          </div>
          <div className="gd-filter-field">
            <label>&nbsp;</label>
            <button
              type="button"
              className={`gd-chip ${onlyNoInvoice ? "active" : ""}`}
              onClick={() => setOnlyNoInvoice((v) => !v)}
            >
              Senza nr fattura
            </button>
          </div>
          <div className="gd-filter-field">
            <label>Totale ricavi</label>
            <div
              style={{
                height: 42,
                display: "flex",
                alignItems: "center",
                padding: "0 12px",
                background: "#fff",
                border: "1px solid #e0e0e0",
                borderRadius: 8,
                fontWeight: 700,
              }}
              title="Totale importi delle righe filtrate"
            >
              {formatCurrency(filteredTotalImporto)}
            </div>
          </div>
        </div>
        {error && <div className="error">{error}</div>}
      </div>

      {fattureRaggruppate.length > 0 && (
        <div className="project-table-wrapper" style={{ marginBottom: 16 }}>
          <table className="project-table">
            <thead>
              <tr
                onClick={() => setShowFattureTable((p) => !p)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setShowFattureTable((p) => !p);
                  }
                }}
                tabIndex={0}
                role="button"
                title={
                  showFattureTable
                    ? "Nascondi tabella fatture"
                    : "Mostra tabella fatture"
                }
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                <th>{showFattureTable ? "▼" : "▶"} Nr fattura</th>
                <th>Cliente</th>
                <th>Righe</th>
                <th style={{ textAlign: "right" }}>Totale</th>
              </tr>
            </thead>
            {showFattureTable && (
              <tbody>
                {fattureRaggruppate.map((f) => (
                  <tr key={f.nrFattura}>
                    <td>{f.nrFattura}</td>
                    <td>{f.cliente || "—"}</td>
                    <td>{f.count}</td>
                    <td style={{ textAlign: "right" }}>
                      {formatCurrency(f.totale)}
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
      )}

      {selectedRowIds.length > 0 && (
        <div className="gd-filters">
          <div className="gd-filter-row">
            <div className="gd-filter-field">
              <label>Nr fattura</label>
              <input
                type="text"
                className="search-input"
                value={bulkNrFattura}
                onChange={(e) => setBulkNrFattura(e.target.value)}
                placeholder="Es. 12/2026"
              />
            </div>
            <div className="gd-filter-field">
              <label>&nbsp;</label>
              <button
                type="button"
                className="gd-tab active"
                onClick={handleBulkAssignInvoice}
                disabled={isAssigningInvoice}
              >
                {isAssigningInvoice ? "Salvataggio..." : "Conferma"}
              </button>
            </div>
            <div className="gd-filter-field">
              <label>&nbsp;</label>
              <div
                style={{ display: "flex", alignItems: "center", height: 42 }}
              >
                {selectedRowIds.length} selezionate
              </div>
            </div>
          </div>
          {bulkError && <div className="error">{bulkError}</div>}
        </div>
      )}

      <div className="project-table-wrapper">
        <table className="project-table">
          <thead>
            <tr>
              <th style={{ width: 46, textAlign: "center" }}>
                <input
                  ref={selectAllRef}
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleSelectAllVisible}
                  aria-label="Seleziona tutte le righe visibili"
                />
              </th>
              <th>Cliente</th>
              <th>Nr fattura</th>
              <th>Riferimento</th>
              <th>Luogo intervento</th>
              <th>Descrizione</th>
              <th>UM</th>
              <th>Importo</th>
              <th style={{ width: 110, textAlign: "right" }}>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ padding: 16, color: "#666" }}>
                  Nessun ricavo trovato per questo progetto.
                </td>
              </tr>
            ) : (
              filteredRows.map((row) => (
                <tr key={row.id}>
                  <td style={{ textAlign: "center" }}>
                    <input
                      type="checkbox"
                      checked={selectedRowIds.includes(row.id)}
                      onChange={() => toggleRowSelected(row.id)}
                      aria-label={`Seleziona riga ${row.id}`}
                    />
                  </td>
                  <td>{row?.cliente || "—"}</td>
                  <td>{row?.nrFattura || "—"}</td>
                  <td>{row?.numero_documento || "—"}</td>
                  <td>{row?.luogoIntervento || "—"}</td>
                  <td>{row?.descrizione || "—"}</td>
                  <td>{row?.unitaMisura || "—"}</td>
                  <td>{formatCurrency(row?.importo)}</td>
                  <td>
                    <div className="actions">
                      <button
                        type="button"
                        className="btn-icon edit"
                        onClick={() => openEditModal(row)}
                        aria-label="Modifica"
                        title="Modifica"
                      >
                        ✎
                      </button>
                      <button
                        type="button"
                        className="btn-icon delete"
                        onClick={() => handleDelete(row)}
                        aria-label="Elimina"
                        title="Elimina"
                        disabled={deletingId === row.id}
                      >
                        ×
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showAddModal && (
        <div className="modal-overlay" onClick={closeAddModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Nuovo ricavo</h3>
              <button
                type="button"
                className="modal-close-btn"
                onClick={closeAddModal}
                aria-label="Chiudi"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {formError && <div className="error">{formError}</div>}

                <div className="form-group">
                  <label>Riferimento</label>
                  <input
                    type="text"
                    value={formState.numero_documento}
                    onChange={(e) =>
                      setFormState((p) => ({
                        ...p,
                        numero_documento: e.target.value,
                      }))
                    }
                    placeholder="Es. DDT 123"
                  />
                </div>

                <div className="form-group">
                  <label>Luogo intervento</label>
                  <input
                    type="text"
                    value={formState.luogoIntervento}
                    onChange={(e) =>
                      setFormState((p) => ({
                        ...p,
                        luogoIntervento: e.target.value,
                      }))
                    }
                    placeholder="Es. Via Roma 10, Milano"
                  />
                </div>

                <div className="form-group">
                  <label>Mese di competenza</label>
                  <input
                    type="month"
                    value={formState.meseCompetenza}
                    onChange={(e) =>
                      setFormState((p) => ({
                        ...p,
                        meseCompetenza: e.target.value,
                      }))
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Descrizione</label>
                  <input
                    type="text"
                    value={formState.descrizione}
                    onChange={(e) =>
                      setFormState((p) => ({
                        ...p,
                        descrizione: e.target.value,
                      }))
                    }
                    placeholder="Es. Installazione quadro elettrico"
                  />
                </div>

                <div className="form-group">
                  <label>Importo</label>
                  <input
                    type="text"
                    value={formState.importo}
                    onChange={(e) =>
                      setFormState((p) => ({ ...p, importo: e.target.value }))
                    }
                    placeholder="Es. 1200,00"
                  />
                </div>
              </div>

              <div className="modal-footer">
                <div
                  className="gd-form-actions"
                  style={{ margin: 0, padding: 0, borderTop: "none" }}
                >
                  <button
                    type="button"
                    className="gd-btn-cancel"
                    onClick={closeAddModal}
                    disabled={isSavingAdd}
                  >
                    Annulla
                  </button>
                  <button
                    type="submit"
                    className="gd-btn-save"
                    disabled={isSavingAdd}
                  >
                    {isSavingAdd ? "Salvataggio..." : "Salva"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && (
        <div className="modal-overlay" onClick={closeEditModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Modifica ricavo</h3>
              <button
                type="button"
                className="modal-close-btn"
                onClick={closeEditModal}
                aria-label="Chiudi"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleEditSubmit}>
              <div className="modal-body">
                {editError && <div className="error">{editError}</div>}

                <div className="form-group">
                  <label>Cliente</label>
                  <input
                    type="text"
                    value={editState.cliente}
                    onChange={(e) =>
                      setEditState((p) => ({ ...p, cliente: e.target.value }))
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Nr fattura</label>
                  <input
                    type="text"
                    value={editState.nrFattura}
                    onChange={(e) =>
                      setEditState((p) => ({ ...p, nrFattura: e.target.value }))
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Riferimento</label>
                  <input
                    type="text"
                    value={editState.numero_documento}
                    onChange={(e) =>
                      setEditState((p) => ({
                        ...p,
                        numero_documento: e.target.value,
                      }))
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Luogo intervento</label>
                  <input
                    type="text"
                    value={editState.luogoIntervento}
                    onChange={(e) =>
                      setEditState((p) => ({
                        ...p,
                        luogoIntervento: e.target.value,
                      }))
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Mese di competenza</label>
                  <input
                    type="month"
                    value={editState.meseCompetenza}
                    onChange={(e) =>
                      setEditState((p) => ({
                        ...p,
                        meseCompetenza: e.target.value,
                      }))
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Descrizione</label>
                  <input
                    type="text"
                    value={editState.descrizione}
                    onChange={(e) =>
                      setEditState((p) => ({
                        ...p,
                        descrizione: e.target.value,
                      }))
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Unità di misura</label>
                  <input
                    type="text"
                    value={editState.unitaMisura}
                    onChange={(e) =>
                      setEditState((p) => ({
                        ...p,
                        unitaMisura: e.target.value,
                      }))
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Importo</label>
                  <input
                    type="text"
                    value={editState.importo}
                    onChange={(e) =>
                      setEditState((p) => ({ ...p, importo: e.target.value }))
                    }
                  />
                </div>
              </div>

              <div className="modal-footer">
                <div
                  className="gd-form-actions"
                  style={{ margin: 0, padding: 0, borderTop: "none" }}
                >
                  <button
                    type="button"
                    className="gd-btn-cancel"
                    onClick={closeEditModal}
                    disabled={isSavingEdit}
                  >
                    Annulla
                  </button>
                  <button
                    type="submit"
                    className="gd-btn-save"
                    disabled={isSavingEdit}
                  >
                    {isSavingEdit ? "Salvataggio..." : "Salva"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {showImportModal && (
        <div className="modal-overlay" onClick={closeImportModal}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 1100 }}
          >
            <div className="modal-header">
              <h3 className="modal-title">Import consuntivi</h3>
              <button
                type="button"
                className="modal-close-btn"
                onClick={closeImportModal}
                aria-label="Chiudi"
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              {importError && <div className="error">{importError}</div>}

              <div className="form-group">
                <label>Mese competenza</label>
                <input
                  type="month"
                  value={consuntiviMonthFilter}
                  onChange={(e) => {
                    setConsuntiviMonthFilter(e.target.value);
                    setSelectedConsuntivoId("");
                    setImportRows([]);
                    setImportError("");
                  }}
                />
              </div>

              <div className="form-group">
                <label>Consuntivo</label>
                <Select
                  classNamePrefix="react-select"
                  styles={selectModalStyles}
                  menuPortalTarget={document.body}
                  options={consuntivoOptions}
                  value={
                    consuntivoOptions.find(
                      (o) => o.value === selectedConsuntivoId,
                    ) || null
                  }
                  onChange={(opt) => {
                    setImportError("");
                    setSelectedConsuntivoId(opt ? opt.value : "");
                  }}
                  isClearable={true}
                  isSearchable={true}
                  placeholder="Seleziona un consuntivo..."
                  noOptionsMessage={() => "Nessun consuntivo trovato"}
                />
              </div>

              {selectedConsuntivoId && (
                <>
                  <div className="form-group">
                    <label>Mese di competenza</label>
                    <input
                      type="month"
                      value={importMeseCompetenza}
                      onChange={(e) => setImportMeseCompetenza(e.target.value)}
                    />
                  </div>

                  {importIsLoading ? (
                    <div style={{ color: "#666" }}>
                      Caricamento interventi...
                    </div>
                  ) : (
                    <div className="project-table-wrapper">
                      <table className="project-table">
                        <thead>
                          <tr>
                            <th>Rif</th>
                            <th>Numero documento</th>
                            <th>Descrizione</th>
                            <th>Importo</th>
                          </tr>
                        </thead>
                        <tbody>
                          {importRows.length === 0 ? (
                            <tr>
                              <td
                                colSpan={4}
                                style={{ padding: 16, color: "#666" }}
                              >
                                Nessuna riga da importare.
                              </td>
                            </tr>
                          ) : (
                            importRows.map((r) => (
                              <tr key={r.key}>
                                <td>{r.rif || "—"}</td>
                                <td>{r.numeroDocumento || "—"}</td>
                                <td>{r.descrizione || "—"}</td>
                                <td>{formatCurrency(r.importo)}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="modal-footer">
              <div
                className="gd-form-actions"
                style={{ margin: 0, padding: 0, borderTop: "none" }}
              >
                <button
                  type="button"
                  className="gd-btn-cancel"
                  onClick={closeImportModal}
                  disabled={importIsLoading || isSavingImport}
                >
                  Annulla
                </button>
                <button
                  type="button"
                  className="gd-btn-save"
                  onClick={handleImport}
                  disabled={
                    importIsLoading ||
                    isSavingImport ||
                    !selectedConsuntivoId ||
                    importRows.length === 0 ||
                    !importMeseCompetenza
                  }
                >
                  {isSavingImport ? "Importazione..." : "Importa"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
